/**
 * Foundational SVG primitives shared by the icon family modules under
 * icons/. Generic element builders only — no family knowledge. Mirrors
 * cards/card-svg.js's role for the card system. Zero deps.
 *
 * Self-rendering contract: every primitive carries its paint as presentation
 * attributes (stroke/fill currentColor), so an icon renders correctly on any
 * surface with zero host CSS — without them, a bare mount fails silently
 * (fill:none + no stroke = invisible). Presentation attributes lose to any
 * CSS rule and to inline styles, so hosts and identity coloring (inline
 * style.fill/.stroke) still override per element.
 */
export const SVG_NS = "http://www.w3.org/2000/svg";

export function svg(width, height, viewBox = "0 0 48 48") {
  const el = document.createElementNS(SVG_NS, "svg");
  el.setAttribute("viewBox", viewBox);
  // Omit width/height when unset so callers can let CSS own the rendered size
  // (e.g. the leaderboard-detail panel's --ld-icon-size clamp) without stamping
  // a literal "undefined" attribute. Mirrors createSuitPipSvg.
  if (width != null) el.setAttribute("width", width);
  if (height != null) el.setAttribute("height", height);
  return el;
}

export function path(d, strokeWidth = 2.5) {
  const el = document.createElementNS(SVG_NS, "path");
  el.setAttribute("d", d);
  el.setAttribute("stroke", "currentColor");
  el.setAttribute("stroke-width", strokeWidth);
  el.setAttribute("stroke-linecap", "round");
  el.setAttribute("stroke-linejoin", "round");
  el.setAttribute("fill", "none");
  return el;
}

export function line(x1, y1, x2, y2, strokeWidth = 2.5) {
  const el = document.createElementNS(SVG_NS, "line");
  el.setAttribute("x1", x1);
  el.setAttribute("y1", y1);
  el.setAttribute("x2", x2);
  el.setAttribute("y2", y2);
  el.setAttribute("stroke", "currentColor");
  el.setAttribute("stroke-width", strokeWidth);
  el.setAttribute("stroke-linecap", "round");
  return el;
}

export function filledPath(d) {
  const el = document.createElementNS(SVG_NS, "path");
  el.setAttribute("d", d);
  el.setAttribute("fill", "currentColor");
  el.setAttribute("stroke", "none");
  return el;
}

/** A filled circle riding currentColor — orb finials, rubble, screw heads. */
export function filledDot(cx, cy, r) {
  const el = document.createElementNS(SVG_NS, "circle");
  el.setAttribute("cx", cx);
  el.setAttribute("cy", cy);
  el.setAttribute("r", r);
  el.setAttribute("fill", "currentColor");
  el.setAttribute("stroke", "none");
  return el;
}

/** Creates a filled SVG element with the given tag, attributes, and fill color. */
export function filled(tag, attrs, color) {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  el.setAttribute("fill", color);
  return el;
}

/** Creates an SVG detail line (internal texture, like leaf veins or bell slit). */
export function detail(tag, attrs) {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  el.setAttribute("fill", "none");
  el.setAttribute("stroke", "#E2E8F0");
  return el;
}
